import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import { GridCloseIcon, GridColDef } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import CustomDataGrid from '../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import { getOrdersBasedTrips } from '../../../redux/reducer/deliveryTrackingSlices/khubtripsSlice';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import constant from '../../../../../utils/constants';
import './Orders.scss';
import {
  capitalizeFirstLetter,
  tripOrOrderStatus
} from '../../../../../utils/commonFunctions';
import ViewOrders from './ViewOrders';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import styled from '@emotion/styled';
import CustomCarousel from '../../../../../common/components/carousel/CustomCarousel';
import { Visibility } from '@mui/icons-material';

interface Order {
  assignedOrderId: string;
  customerName: string;
  contactNumber: string;
  deliverableType: string;
  noOfBoxes: number;
  status: string;
  imageURL: string;
}
type OrderProps = {
  tripId: number;
};

function Orders({ tripId }: OrderProps) {
  const dispatch = useAppDispatch();

  const BlurryDialog = styled(Dialog)<DialogProps>(({ theme }) => ({
    backgroundColor: 'rgba(60, 60, 60, 0.5)'
  }));

  const orderData = useAppSelector(state => state.khubtriporders);

  const [orderDetails, setOrderDetails] = useState<[] | null>(null);
  const [viewBarcode, setViewBarcode] = useState(false);
  const [viewBarcodeData, setViewBarcodeData] = useState<any>();

  const [selectedRow, setSelectedRow] = useState<Order | null>(null);
  const [viewImage, setViewImage] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewOrders, setViewOrders] = useState(false);
  const openMenu = Boolean(anchorEl);

  const columns: GridColDef[] = [
    { field: 'assignedOrderId', headerName: 'Order ID', minWidth: 100, flex: 1 },
    { field: 'customerName', headerName: 'Customer Name', minWidth: 100, flex: 1 },
    { field: 'contactNumber', headerName: 'Contact Number', minWidth: 100, flex: 1 },
    {
      field: 'deliverableType',
      headerName: 'Order Type',
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) => {
        let removeChar = row?.deliverableType?.split('_').join(' ');
        return removeChar
          .toLowerCase()
          .replace(/(?:^|\s)\w/g, (match: string) => match.toUpperCase());
      }
    },
    {
      field: 'noOfBoxes',
      headerName: 'No. of Boxes',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => row?.noOfBoxes || 0
    },
    {
      field: 'status',
      headerName: 'Order Status',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => tripOrOrderStatus(row?.status)
    },
    {
      field: 'barcodeMapList',
      headerName: 'View Barcode',
      minWidth: 100,
      renderCell: ({ row }: any) => (
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() => handleBarcodeView(row.barcodeMapList)}
        >
          <Visibility sx={{ mr: 1, cursor: 'pointer', color: 'rgb(0 0 255)' }} />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Images',
      minWidth: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: params => {
        const viewedRow = params.row;
        return viewedRow?.imageURL ? (
          <Icon
            icon='flowbite:file-image-outline'
            className='menu-bg'
            color='#3239ea'
            onClick={() => {
              setSelectedRow(viewedRow);
              setViewImage(true);
            }}
          />
        ) : (
          'NA'
        );
      }
    }
  ];

  const handleBarcodeView = (params: any) => {
    setViewBarcode(true);
    setViewBarcodeData(params);
  };

  useEffect(() => {
    dispatch(getOrdersBasedTrips(tripId));
  }, [tripId]);

  useEffect(() => {
    if (orderData) {
      let data: any = orderData?.data?.map((item: any) => ({
        ...item,
        id: item?.assignedOrderId,
        customerName: capitalizeFirstLetter(item?.customerName)
      }));
      setOrderDetails(data);
    }
  }, [orderData]);

  return (
    <Box m={1}>
      <CustomDataGrid
        columns={columns}
        rows={orderDetails?.length ? orderDetails : []}
        loading={orderData.isLoading}
      />
      {viewBarcode && (
        <Dialog
          open={viewBarcode}
          maxWidth='xs'
          fullWidth
          sx={{ padding: 3, zIndex: 1, position: 'absolute' }}
          slotProps={{ backdrop: { invisible: true } }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id='customized-dialog-title'>
            Barcode
          </DialogTitle>
          <IconButton
            aria-label='close'
            onClick={() => {
              setViewBarcode(false);
              setViewBarcodeData([]);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <GridCloseIcon />
          </IconButton>
          <Grid container p={2}>
            {viewBarcodeData.map((item: any) => (
              <Grid item xs={6} display={'flex'} mb={1}>
                <Box>
                  <Icon
                    icon='weui:qr-code-outlined'
                    width='24'
                    height='24'
                    style={{ color: 'rgb(66 66 255)' }}
                  />
                </Box>
                <Box ml={2}>{item.barcodeId}</Box>
              </Grid>
            ))}
          </Grid>
        </Dialog>
      )}

      {viewImage && (
        <BlurryDialog
          open={true}
          fullWidth
          fullScreen
          maxWidth='md'
          className='dialog'
          onClose={() => setViewImage(false)}
          PaperProps={{
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }
          }}
        >
          <DialogContent
            className='dialogContent'
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box className='closeIcon icon-position d-flex'>
              <CustomIconButton
                category='CloseValue'
                onClick={() => {
                  setViewImage(false);
                }}
              />
            </Box>
            <CustomCarousel image={[selectedRow?.imageURL]} />
          </DialogContent>
        </BlurryDialog>
      )}
    </Box>
  );
}

export default Orders;
